/**
 * HITL-1646 — how the agent first touched My Cart.
 *
 * Agent engagement is the stated objective of Supervised AI mode, and there is
 * no longer a mode change that measures it. A third mode used to sit in front of
 * `Shadow`, so leaving it was a signal that the agent had started working;
 * HITL-1604 shipped two modes with `Shadow` as the resting state, so every agent
 * is in `Shadow` whether they are working or not.
 *
 * What replaces it is better than what it replaced: a mode change measured a
 * button press at the start of a shift, this measures the first time the agent
 * actually did something.
 */
export type AgentFirstCartEditVia =
  /** "Copy all" took the whole Live Order across. */
  | 'copy_all'
  /** One `«` on a Live Order card. */
  | 'copy_item'
  /** The agent typed an item in themselves. */
  | 'omnibar_add'
  /**
   * A modifier, size or option pick — including the centre pane's — or a count
   * on one (HITL-1753).
   */
  | 'modifier'
  /** A step on a My Cart line's quantity. */
  | 'quantity'
  /** Duplicate on a My Cart line. */
  | 'duplicate'
  /** Remove on a My Cart line. */
  | 'delete';

/**
 * Draft-cart action types that are unambiguously one kind of edit.
 *
 * `draftCart/addItemToCart` is deliberately ABSENT: three different journeys end
 * in it — Copy all, a per-item copy and an omnibar add — and telling them apart
 * is the whole point of `via`. Those three tag the dispatch itself; see
 * `resolveFirstCartEditVia`.
 *
 * Keyed on the action's string type rather than on the imported action creators
 * so this module stays free of slice imports — it is read by a middleware that
 * must not pull the store's own graph in behind it.
 */
export const DRAFT_CART_EDIT_VIA: Readonly<
  Record<string, AgentFirstCartEditVia>
> = {
  'draftCart/selectModifier': 'modifier',
  'draftCart/selectModifiers': 'modifier',
  'draftCart/applyOptionReplacement': 'modifier',
  'draftCart/setModifierQuantity': 'modifier',
  'draftCart/updateQuantity': 'quantity',
  'draftCart/cloneCartItem': 'duplicate',
  'draftCart/deleteCartItem': 'delete',
};

/** The one ambiguous action; its `via` rides on the payload. */
export const DRAFT_CART_ADD_ACTION = 'draftCart/addItemToCart';

/**
 * What kind of edit is this action, if it is one at all?
 *
 * Returns `undefined` for everything that is not a write into My Cart — which
 * is the overwhelming majority of actions, so this is the hot path's early exit.
 *
 * `setCartItemCollapseState` and `setExplicitExpandState` are NOT edits. Folding
 * a card open is reading, not working, and counting it would make the engagement
 * number say the opposite of what it claims.
 */
export const resolveFirstCartEditVia = (action: {
  type?: unknown;
  payload?: unknown;
}): AgentFirstCartEditVia | undefined => {
  const type = typeof action?.type === 'string' ? action.type : '';
  if (type === DRAFT_CART_ADD_ACTION) {
    const via = (action?.payload as { via?: AgentFirstCartEditVia })?.via;
    // The two copy journeys tag themselves (`draftCart.thunks`). The omnibar
    // does not, deliberately: it reaches this action through
    // `menuSlice.addItemToCart`, a path the AI's own replay also uses, and
    // threading a tag down it would mean touching the one function where a
    // mistake puts the agent's marker on the AI's work. So `omnibar_add` is the
    // residual BY DESIGN — the agent typed it in — and a future untagged writer
    // into the draft would have to declare itself here.
    return via ?? 'omnibar_add';
  }
  return DRAFT_CART_EDIT_VIA[type];
};
